/**
 * MarketingAttribution — the one spelling of the attribution wire contract.
 *
 * Read by the backend validator (`parseAttributionBundle`), the `POST /organizations`
 * TypeBox schema and the SPA capture module. The nine keys are a single list here so
 * the reader on each side of the wire cannot drift from the other.
 *
 * See docs/Plans/2026-08-15-ga4-funnel-events.md §4.4 and §5.1.
 */
/**
 * Machine-generated identifiers. Strict charset — see
 * `MARKETING_ATTRIBUTION_IDENTIFIER_PATTERN`.
 */
export const MARKETING_ATTRIBUTION_IDENTIFIER_FIELDS = [
    "client_id",
    "session_id",
    "gclid",
    "fbclid",
];
/**
 * Human-authored campaign text. Permissive charset — a Brazilian campaign is
 * spelled `lançamento safra 2026`, and an identifier-grade filter would discard it.
 */
export const MARKETING_ATTRIBUTION_CAMPAIGN_TEXT_FIELDS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
];
/**
 * The nine keys of the bundle, derived from the two charset classes so the
 * partition is total by construction: a field can never be added to the wire
 * contract without also being assigned a validation rule.
 */
export const MARKETING_ATTRIBUTION_FIELDS = [
    ...MARKETING_ATTRIBUTION_IDENTIFIER_FIELDS,
    ...MARKETING_ATTRIBUTION_CAMPAIGN_TEXT_FIELDS,
];
/*
 * The two caps below have DISTINCT jobs and must not be conflated — conflating
 * them makes the parser's "drop, never fail" promise unreachable, because route
 * validation runs first and would have already rejected the request.
 *
 *   MARKETING_ATTRIBUTION_ABUSE_CAP   → route/TypeBox layer → violation is a 400.
 *   MARKETING_ATTRIBUTION_MAX_LENGTH  → parser layer        → violation DROPS that
 *                                                             one field and keeps
 *                                                             the rest; the org is
 *                                                             still created.
 *
 * The abuse cap therefore sits strictly ABOVE every semantic cap. A 5 000-character
 * `utm_term` is not a marketing value, it is a payload; a 200-character one is a
 * sloppy campaign name and costs us a column, not a signup.
 */
/**
 * The single route-level ceiling applied to every field by the `POST /organizations`
 * TypeBox schema. An abuse ceiling, not a semantic one — violation is a 400
 * (`VALIDATION_ERROR`): the app-wide error handler maps every Elysia VALIDATION
 * to 400, so the plan's stated 422 never reaches a client.
 */
export const MARKETING_ATTRIBUTION_ABUSE_CAP = 512;
/**
 * Per-field SEMANTIC caps, applied by `parseAttributionBundle`. A value over its cap
 * is dropped from the bundle; the remaining fields are kept and the organization is
 * created. Never a request failure — see the note above.
 */
export const MARKETING_ATTRIBUTION_MAX_LENGTH = {
    client_id: 64,
    session_id: 64,
    gclid: 256,
    fbclid: 256,
    utm_source: 128,
    utm_medium: 128,
    utm_campaign: 128,
    utm_term: 128,
    utm_content: 128,
};
/**
 * Charset for `MARKETING_ATTRIBUTION_IDENTIFIER_FIELDS`. These values are emitted by
 * Google/Meta tooling, so the strict form is the correct one — anything else in the
 * field is not an identifier that came from a tag.
 */
export const MARKETING_ATTRIBUTION_IDENTIFIER_PATTERN = /^[\w.\-~%+]+$/;
/**
 * Unicode control characters, stripped from
 * `MARKETING_ATTRIBUTION_CAMPAIGN_TEXT_FIELDS` before the trim/non-empty/cap checks.
 * Accented Portuguese, spaces and ordinary punctuation survive; log-injection and
 * terminal-escape payloads do not.
 *
 * The `g` flag makes this stateful (`lastIndex`), so use it with `String.replace`
 * only — never `.test()` on this shared instance.
 */
export const MARKETING_ATTRIBUTION_CONTROL_CHARACTERS = /\p{C}/gu;
/**
 * The domain records whose first creation activates an organization.
 *
 * Five kinds rather than the spec's three: the report needs to tell "they weighed
 * cattle" from "they logged a pasture reading". A batch commit reports the same kind
 * as its single-record sibling, because it is the same user intent.
 */
export const ACTIVATION_RECORD_KINDS = [
    "weighing", // livestockService.createMeasurement / createMeasurementsBatch
    "handling", // livestockService.createOperation / createOperationsBatch
    "pasture_measurement", // measurementService.createMeasurement
    "pasture_operation", // operationService.createOperation
    "cost_entry", // transactionService.createManualEntry / createFromReceipt
];
//# sourceMappingURL=MarketingAttribution.js.map